class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResultsAndFavoritesContainer = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    
    this.overlay = this.querySelector('.predictive-search-overlay');
    this.resultsContainer = this.predictiveSearchResultsAndFavoritesContainer.querySelector('.ps-results-container');
    this.favoritesContainer = this.predictiveSearchResultsAndFavoritesContainer.querySelector('.ps-favorites-wrapper');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.breakpoint = this.dataset.breakpoint;
    this.resizeObserver = null;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onInputClick.bind(this));
    this.input.addEventListener('input', debounce(this.onChange.bind(this), 300));
    this.resetButton.addEventListener('click', this.close.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.overlay?.addEventListener('click', this.close.bind(this));
  }

  startResizeObserver() {
    if (this.resizeObserver) return;
    this.resizeObserver = new ResizeObserver(() => {
      this.setResultsHeight();
      if (this.isOpen && (this.breakpoint === "mobile" && window.innerWidth >= 750) || (this.breakpoint === "desktop" && window.innerWidth < 750)) {
        this.close(true);
      }
    });
    this.resizeObserver.observe(document.documentElement);
  }

  stopResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  onInputClick() {
    if (!this.isOpen) {
      this.open();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.resultsContainer.querySelector('#predictive-search-results')?.remove();
    }
    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if(!this.searchTerm){
      //Load Favorites
      this.removeAttribute('results');
    }else{
      this.getSearchResults(this.searchTerm);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  // onFormReset(event) {
  //   super.onFormReset(event);
  //   if (super.shouldResetForm()) {
  //     this.searchTerm = '';
  //     this.abortController.abort();
  //     this.abortController = new AbortController();
  //     this.closeResults(true);
  //   }
  // }

  onFocus() {
    const newSearchTerm = this.getQuery();
    if (!this.isOpen) {
      //it's not open, so open it
      this.open();
    }
    if (this.searchTerm !== newSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)){
        setTimeout(() => {
        this.resetButton?.classList.toggle('animate-horizontal-shaking', true);
        }, 300);
        setTimeout(() => {
          this.resetButton?.classList.toggle('animate-horizontal-shaking', false);
        }, 700);
      }
    },0);
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close(true);
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'))
      if (matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item, suggested-searchterm')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    //Find index of the current aria-selected=true element
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"], suggested-searchterm[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setEventListenerForHistory() {
    const area = this.resultsContainer.querySelector('.predictive-search-products');
    const searchHistoryElement = this.querySelector('predictive-search-history');
    if(!area || !searchHistoryElement) return;

    area.addEventListener('click', (event) => {
      // Prüfen, ob das geklickte Element ein Link (<a>) ist
      if (event.target.tagName !== 'A') return;
      searchHistoryElement.saveSearchTermInHistory(this.searchTerm);
  });

}


  renderSearchResults(resultsMarkup) {
    this.resultsContainer.innerHTML = resultsMarkup.innerHTML;
    this.setEventListenerForHistory();
    this.toggleAttribute('results', !resultsMarkup.querySelector(".empty-search-message-wrapper"));

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  setResultsHeight(heightPx = null) {
    this.resultsHeight = window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    const currentHeightString = this.predictiveSearchResultsAndFavoritesContainer.style.height;
    const currentHeightInt = parseInt(currentHeightString?.replace('px', ''));
    if(parseInt(this.resultsHeight) == currentHeightInt)return;
    this.predictiveSearchResultsAndFavoritesContainer.style.height = `${this.resultsHeight}px`;
  }

  open() {
    document.body.classList.toggle('overflow-hidden' + "-" + this.breakpoint, true);
    this.setResultsHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.startResizeObserver();
  }

  close(clearSearchTerm = false) {
    document.body.classList.toggle('overflow-hidden' + "-" + this.breakpoint, false);
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.stopResizeObserver();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsHeight = false;
    //this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);


class PredictiveSearchHistory extends HTMLElement {
  constructor() {
    super();
    this.listContainer = this.querySelector('#predictive-search-search-history-list');
    this.template = this.listContainer?.querySelector('[template]');
    this.checkGetParams();
  }

  checkGetParams(){
    if(window.location.pathname !== "/search") return;
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = urlParams.get('q');
    if(searchTerm){
      this.saveSearchTermInHistory(searchTerm);
    }
  }

  pushIntoDom(term){
    const clone = this.template.cloneNode(true);
    clone.setAttribute('data-searchterm', term);
    clone.removeAttribute('template');
    const textElement = clone.querySelector('p');
    textElement.textContent = term;
    this.listContainer.appendChild(clone);
  }

  getLocalStorageData() {
    return JSON.parse(localStorage.getItem("hc_search_history") || "[]");
  }

  setLocalStorageData(data) {
    localStorage.setItem("hc_search_history", JSON.stringify(data));
  }

  connectedCallback() {
    const data = this.getLocalStorageData();
    this.classList.toggle('hidden', !data.length);
    data.forEach((term) => {
      this.pushIntoDom(term);
    });
  }

  saveSearchTermInHistory(searchTerm){
    if(!searchTerm?.trim()) return;

    // Den aktuellen Suchbegriff in LocalStorage speichern
    let currentData = this.getLocalStorageData();

    // Index des Suchbegriffs im Array finden
    const alreadyExistsIndex = currentData.indexOf(searchTerm);

    if (alreadyExistsIndex !== -1) {
        // Wenn der Suchbegriff bereits existiert, entferne ihn
        currentData.splice(alreadyExistsIndex, 1);
    }

    // Den Suchbegriff an den Anfang des Arrays setzen
    currentData.unshift(searchTerm);

    // Sicherstellen, dass das Array maximal 10 Elemente hat
    if (currentData.length > 10) {
        currentData = currentData.slice(0, 10);
    }

    // Aktualisiertes Array im LocalStorage speichern
    this.setLocalStorageData(currentData);
  }
}

customElements.define('predictive-search-history', PredictiveSearchHistory);